import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Popover, Paper, List, ListItemButton, ListItemText, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

const OPTION_PASS = 'PASS';

/**
 * 작업환경측정 상반기/하반기 셀 에디터
 * - 셀 클릭 시 셀 기준 팝오버로 콤보(PASS / 측정일) 표시
 * - PASS 선택 → 셀에 'PASS' 바인딩
 * - 측정일 선택 → 바로 달력 표시, 날짜 선택 시 셀에 날짜 바인딩
 */
const PassOrDateCellEditor = forwardRef((props, ref) => {
  const { stopEditing, api } = props;
  const anchorRef = useRef(null);
  const currentValueRef = useRef(props.value != null && props.value !== '' ? props.value : '');
  const propsRef = useRef(props);
  propsRef.current = props;

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [viewMode, setViewMode] = useState('choice'); // 'choice' | 'date'
  const [dateValue, setDateValue] = useState(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setPopoverOpen(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => currentValueRef.current ?? '',
  }));

  const getColumnField = () => {
    const col = propsRef.current.column;
    if (col?.getColDef?.()?.field) return col.getColDef().field;
    if (propsRef.current.colDef?.field) return propsRef.current.colDef.field;
    if (col?.getColId?.()) return col.getColId();
    return null;
  };

  const applyValueToRow = (newVal) => {
    const n = propsRef.current.node;
    const f = getColumnField();
    if (!n || !f) return;
    const rowData = n.data;
    const updatedRow = { ...rowData, [f]: newVal };
    api?.applyTransaction?.({ update: [updatedRow] });
    n.setDataValue(f, newVal);
    if (api?.refreshCells) {
      api.refreshCells({ rowNodes: [n], force: true });
    }
  };

  const applyAndClose = (newVal) => {
    if (newVal == null || newVal === '') return;
    currentValueRef.current = newVal;
    setPopoverOpen(false);
    applyValueToRow(newVal);
    stopEditing?.(false);
  };

  const handleChoosePass = () => {
    currentValueRef.current = OPTION_PASS;
    setPopoverOpen(false);
    applyValueToRow(OPTION_PASS);
    stopEditing?.(false);
  };

  const handleChooseDate = () => {
    setViewMode('date');
    const v = props.value;
    if (v && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && dayjs(v).isValid()) {
      setDateValue(dayjs(v));
    } else {
      // value가 없을 때는 선택 상태를 비워 두고,
      // 오늘 날짜는 달력에서 기본 하이라이트만 되도록 둔다.
      // 이렇게 하면 오늘이 기본으로 보이더라도 오늘 칸을 클릭해서 선택할 수 있다.
      setDateValue(null);
    }
  };

  const handleDateChange = (date) => {
    // 달력에서 연/월/일을 조정할 때마다 내부 값만 갱신하고, 즉시 닫지는 않음
    if (date && dayjs(date).isValid()) {
      setDateValue(dayjs(date));
    }
  };

  const handleAccept = (date) => {
    // 사용자가 날짜를 확정(OK)했을 때만 셀에 바인딩하고 닫기
    if (date && dayjs(date).isValid()) {
      applyAndClose(dayjs(date).format('YYYY-MM-DD'));
    }
  };

  const handleBack = () => {
    setViewMode('choice');
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
    setViewMode('choice');
    stopEditing?.(true);
  };

  const popoverContent = (
    <Paper elevation={8} sx={{ minWidth: 200, overflow: 'hidden' }}>
      {viewMode === 'choice' ? (
        <List dense disablePadding>
          <ListItemButton onClick={handleChoosePass}>
            <ListItemText primary="PASS" primaryTypographyProps={{ fontSize: '0.875rem' }} />
          </ListItemButton>
          <ListItemButton onClick={handleChooseDate}>
            <ListItemText primary="측정일" primaryTypographyProps={{ fontSize: '0.875rem' }} />
          </ListItemButton>
        </List>
      ) : (
        <>
          <List dense disablePadding sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <ListItemButton onClick={handleBack} sx={{ py: 0.5 }}>
              <IconButton size="small" sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
              <ListItemText primary="날짜 선택" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </ListItemButton>
          </List>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <StaticDatePicker
              value={dateValue}
              onChange={handleDateChange}
              onAccept={handleAccept}
              format="YYYY-MM-DD"
              displayStaticWrapperAs="desktop"
              slotProps={{ actionBar: { actions: ['today', 'accept', 'cancel'] } }}
            />
          </LocalizationProvider>
        </>
      )}
    </Paper>
  );

  return (
    <>
      <div ref={anchorRef} style={{ width: '100%', height: '100%', minHeight: 32 }} />
      {popoverOpen &&
        createPortal(
          <>
            <Popover
              open={popoverOpen}
              anchorEl={anchorRef.current}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableScrollLock
              slotProps={{ root: { sx: { zIndex: 10000 } } }}
              PaperProps={{ sx: { zIndex: 10001 } }}
              ModalProps={{ disableScrollLock: true, sx: { zIndex: 10000 } }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {popoverContent}
            </Popover>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
              onMouseDown={handlePopoverClose}
              onClick={handlePopoverClose}
            />
          </>,
          document.body
        )}
    </>
  );
});

PassOrDateCellEditor.displayName = 'PassOrDateCellEditor';

export default PassOrDateCellEditor;
